/**
 * TaggrManager.ts - Tag Interface Layer over Organizr
 * 
 * CHANGE HISTORY:
 * - 251102_0631 [pending] danielbeer: Rebuilt as interface layer over Organizr
 * 
 * ARCHITECTURE: Taggr + Organizr Integration
 * 
 * Organizr = Source of truth for tag lists (data layer)
 * Taggr = UI/interaction layer for displaying and managing tags
 * 
 * Taggr provides:
 * - Tag-specific UI components (TagInput, TagList)
 * - Tag-specific operations (tagging items, searching by tags)
 * - Simplified tag API over Organizr's list API
 * 
 * Organizr provides:
 * - Data storage for tag lists
 * - Hierarchical tag structures
 * - CRUD operations via Actr
 * - Persistence via Aggregatr
 * 
 * Uses ALL 5 core orchestrators:
 * 1. Actr - Via Organizr (CRUD operations)
 * 2. Interfacr - UI operations (dialogs, notifications)
 * 3. Aggregatr - Via Organizr (data persistence)
 * 4. Integratr - Not needed
 * 5. Synkr - Not needed initially
 */

import type { OrganizrManager } from '@opcentral/organizr';
import type {
  Tag,
  TagGroup,
  TaggrOptions,
  TagOptions,
  GroupOptions,
  SearchOptions
} from './types';

export class TaggrManager {
  // Core dependencies
  private organizr: OrganizrManager;
  private _interfacr: any; // Reserved for future use (error notifications)
  
  // Configuration
  private tagListId: string;
  private options: { caseSensitive: boolean; allowDuplicates: boolean; maxTagLength: number };
  
  // Cache for performance
  private tagCache: Map<string, Tag> = new Map();
  private itemTagsCache: Map<string, Set<string>> = new Map();

  constructor(options: TaggrOptions) {
    if (!options.organizr) {
      throw new Error('TaggrManager requires an Organizr instance');
    }

    this.organizr = options.organizr;
    this._interfacr = options.interfacr;
    this.tagListId = options.workspaceId || 'default-tags';
    
    this.options = {
      caseSensitive: options.caseSensitive ?? false,
      allowDuplicates: options.allowDuplicates ?? false,
      maxTagLength: options.maxTagLength ?? 50
    };
    
    // Ensure tag list exists in Organizr
    this.initializeTagList();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize or get the tag list from Organizr
   */
  private initializeTagList(): void {
    let tagList = this.organizr.getList(this.tagListId);
    
    if (!tagList) {
      // Create the tag list in Organizr
      tagList = this.organizr.createList({
        id: this.tagListId,
        name: 'Tags',
        type: 'tags',
        color: '#6366f1',
        icon: '🏷️',
        allowOther: true,
        children: []
      });
    }
    
    // Build initial cache
    this.rebuildCache();
  }

  /**
   * Rebuild tag cache from Organizr
   */
  private rebuildCache(): void {
    this.tagCache.clear();
    
    const tagList = this.organizr.getList(this.tagListId);
    if (!tagList) return;

    // Convert Organizr items to Tags
    const convertItem = (item: any, parent: string | null = null): void => {
      const tag: Tag = {
        id: item.id,
        name: item.value || item.name.toLowerCase(),
        displayName: item.name,
        color: item.color || '#6366f1',
        icon: item.icon || null,
        group: parent,
        description: item.metadata?.description || '',
        metadata: {
          created: new Date(item.metadata?.createdAt || Date.now()),
          updated: new Date(item.metadata?.updatedAt || Date.now()),
          usageCount: item.metadata?.usageCount || 0
        },
        relationships: {
          parent,
          children: item.children?.map((c: any) => c.value || c.name.toLowerCase()) || [],
          related: item.metadata?.related || []
        }
      };

      this.tagCache.set(tag.name, tag);

      // Process children recursively
      if (item.children) {
        item.children.forEach((child: any) => convertItem(child, tag.name));
      }
    };

    tagList.children.forEach((item: any) => convertItem(item));
  }

  // ============================================================================
  // TAG CRUD (via Organizr)
  // ============================================================================

  /**
   * Create a new tag
   */
  async createTag(name: string, options: TagOptions = {}): Promise<Tag> {
    const normalizedName = this.normalizeName(name);

    // Validation
    if (!normalizedName) {
      throw new Error('Tag name cannot be empty');
    }

    if (normalizedName.length > this.options.maxTagLength) {
      throw new Error(`Tag name exceeds maximum length of ${this.options.maxTagLength}`);
    }

    if (this.tagCache.has(normalizedName) && !this.options.allowDuplicates) {
      throw new Error(`Tag "${name}" already exists`);
    }

    // Create item in Organizr
    const parentId = options.parent ? this.findItemIdByName(options.parent) : null;
    
    (this.organizr as any).addItem(this.tagListId, {
      name,
      value: normalizedName,
      color: options.color || this.generateColor(),
      icon: options.icon || null,
      metadata: {
        description: options.description || '',
        usageCount: 0,
        related: options.related || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    }, parentId);

    // Rebuild cache
    this.rebuildCache();

    return this.getTag(normalizedName)!;
  }

  /**
   * Get a tag by name or ID
   */
  getTag(nameOrId: string): Tag | null {
    const normalized = this.normalizeName(nameOrId);
    
    // Try by name first
    if (this.tagCache.has(normalized)) {
      return this.tagCache.get(normalized)!;
    }

    // Try by ID
    for (const tag of this.tagCache.values()) {
      if (tag.id === nameOrId) {
        return tag;
      }
    }

    return null;
  }

  /**
   * Update a tag
   */
  async updateTag(nameOrId: string, updates: Partial<Tag>): Promise<Tag> {
    const tag = this.getTag(nameOrId);
    
    if (!tag) {
      throw new Error(`Tag "${nameOrId}" not found`);
    }

    // Update in Organizr
    this.organizr.updateItem(this.tagListId, tag.id, {
      name: updates.displayName || tag.displayName,
      color: updates.color || tag.color,
      icon: updates.icon !== undefined ? updates.icon : tag.icon,
      metadata: {
        description: updates.description || tag.description,
        usageCount: updates.metadata?.usageCount || tag.metadata.usageCount,
        updatedAt: new Date().toISOString()
      }
    });

    // Rebuild cache
    this.rebuildCache();

    return this.getTag(tag.name)!;
  }

  /**
   * Delete a tag
   */
  async deleteTag(nameOrId: string): Promise<boolean> {
    const tag = this.getTag(nameOrId);
    
    if (!tag) {
      return false;
    }

    // Delete from Organizr
    this.organizr.deleteItem(this.tagListId, tag.id);

    // Clear from caches
    this.tagCache.delete(tag.name);
    
    // Remove from all items
    for (const [itemId, tags] of this.itemTagsCache.entries()) {
      if (tags.has(tag.name)) {
        tags.delete(tag.name);
      }
    }

    return true;
  }

  /**
   * Get all tags
   */
  getAllTags(): Tag[] {
    return Array.from(this.tagCache.values());
  }

  /**
   * Search tags
   */
  searchTags(query: string): Tag[] {
    const normalized = this.normalizeName(query);
    
    return this.getAllTags().filter(tag =>
      tag.name.includes(normalized) ||
      tag.displayName.toLowerCase().includes(query.toLowerCase()) ||
      tag.description.toLowerCase().includes(query.toLowerCase())
    );
  }

  // ============================================================================
  // TAG GROUPS (via Organizr nested structure)
  // ============================================================================

  /**
   * Create a tag group (nested tag in Organizr)
   */
  async createGroup(name: string, options: GroupOptions = {}): Promise<TagGroup> {
    // Groups are just tags with children in Organizr
    const tag = await this.createTag(name, {
      ...options,
      parent: options.parent
    });

    return {
      id: tag.id,
      name: tag.name,
      color: tag.color,
      icon: tag.icon,
      description: tag.description,
      tags: new Set(),
      parent: tag.relationships.parent,
      children: []
    };
  }

  /**
   * Get all groups (tags with children)
   */
  getAllGroups(): TagGroup[] {
    return this.getAllTags()
      .filter(tag => tag.relationships.children.length > 0)
      .map(tag => ({
        id: tag.id,
        name: tag.name,
        color: tag.color,
        icon: tag.icon,
        description: tag.description,
        tags: new Set(tag.relationships.children),
        parent: tag.relationships.parent,
        children: tag.relationships.children
      }));
  }

  // ============================================================================
  // ITEM TAGGING (stored in item metadata via Organizr)
  // ============================================================================

  /**
   * Tag an item
   */
  async tagItem(itemId: string, tags: string | string[]): Promise<void> {
    const tagArray = Array.isArray(tags) ? tags : [tags];

    for (const tagName of tagArray) {
      const normalized = this.normalizeName(tagName);

      // Create tag if it doesn't exist
      if (!this.tagCache.has(normalized)) {
        await this.createTag(tagName);
      }

      // Add to cache
      if (!this.itemTagsCache.has(itemId)) {
        this.itemTagsCache.set(itemId, new Set());
      }
      this.itemTagsCache.get(itemId)!.add(normalized);

      // Update usage count
      const tag = this.getTag(normalized);
      if (tag) {
        await this.updateTag(normalized, {
          metadata: {
            ...tag.metadata,
            usageCount: tag.metadata.usageCount + 1
          }
        });
      }
    }
  }

  /**
   * Remove tag from item
   */
  async untagItem(itemId: string, tagName: string): Promise<void> {
    const normalized = this.normalizeName(tagName);

    const itemTags = this.itemTagsCache.get(itemId);
    if (itemTags) {
      itemTags.delete(normalized);
      if (itemTags.size === 0) {
        this.itemTagsCache.delete(itemId);
      }
    }

    // Update usage count
    const tag = this.getTag(normalized);
    if (tag && tag.metadata.usageCount > 0) {
      await this.updateTag(normalized, {
        metadata: {
          ...tag.metadata,
          usageCount: tag.metadata.usageCount - 1
        }
      });
    }
  }

  /**
   * Get tags for an item
   */
  getItemTags(itemId: string): string[] {
    const tagSet = this.itemTagsCache.get(itemId);
    return tagSet ? Array.from(tagSet) : [];
  }

  /**
   * Search items by tags
   */
  searchByTags(tags: string[], options: SearchOptions = {}): string[] {
    const normalizedTags = tags.map(t => this.normalizeName(t));
    const matchAll = options.matchAll ?? true;

    const allItems = new Set<string>();
    const itemCounts = new Map<string, number>();

    for (const [itemId, itemTags] of this.itemTagsCache.entries()) {
      let matches = 0;
      for (const tag of normalizedTags) {
        if (itemTags.has(tag)) {
          matches++;
        }
      }

      if (matches > 0) {
        allItems.add(itemId);
        itemCounts.set(itemId, matches);
      }
    }

    if (matchAll) {
      return Array.from(allItems).filter(itemId =>
        itemCounts.get(itemId) === normalizedTags.length
      );
    } else {
      return Array.from(allItems);
    }
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Normalize tag name
   */
  private normalizeName(name: string): string {
    const normalized = name.trim();
    return this.options.caseSensitive ? normalized : normalized.toLowerCase();
  }

  /**
   * Find Organizr item ID by tag name
   */
  private findItemIdByName(name: string): string | null {
    const tag = this.getTag(name);
    return tag ? tag.id : null;
  }

  /**
   * Generate random color
   */
  private generateColor(): string {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
      '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  /**
   * Get statistics
   */
  getStats(): { totalTags: number; totalGroups: number; totalTaggedItems: number } {
    return {
      totalTags: this.tagCache.size,
      totalGroups: this.getAllGroups().length,
      totalTaggedItems: this.itemTagsCache.size
    };
  }
}

// Singleton instance
let instance: TaggrManager | null = null;

export function getTaggr(options: TaggrOptions): TaggrManager {
  if (!instance) {
    instance = new TaggrManager(options);
  }
  return instance;
}

export function resetTaggr(): void {
  instance = null;
}
